import React, {useEffect, useMemo, useState} from "react";
import {Typography} from "@mui/material";

import {makeCresAxiosFoundationAPI} from "../../util/AxiosFoundationAPI";
import {Grey5, Grey7, Grey17} from "../../theme/styles";

// Who ELSE can reach this record, on the Detail tab, read-only.
//
// THE PRIMARIES SIT ABOVE THIS and come back with the record already joined. Everything else that
// can reach it is a membership row, and those are one-to-many: no EnterpriseComponent in the
// platform carries an aggregate, so they cannot be a joined attribute the way the primaries are.
// Hence a small read of its own rather than a wider payload.
//
// READING, NOT MANAGING. The Visibility tab adds, removes and levels shares; this exists so that
// reading the record does not require going there. Ed, 2026-09-21: "I just want the user to easily
// see the data on the detail tab so going to the Visibility tab is needed less." Two screens that
// can both CHANGE a thing is how they come to disagree; two that can both SHOW it is convenience.
//
// ALWAYS SHOWN, EVEN WHEN EMPTY. This drew nothing at all when a record was shared with nobody, on
// the reasoning that three empty labels are noise. Ed's call, 2026-09-21: "It is ok to show
// Secondary Positions -". He is right, and the reasoning I used was wrong in a specific way -- a
// field that disappears cannot be read as "none", only as "not here", so somebody checking whether
// a record is shared learns nothing from its absence. An em-dash answers the question; a missing
// row leaves them to go to the Visibility tab, which is the trip this line exists to save.
//
// The dash is the SAME em-dash the Field helper above uses for an empty value, so the block reads
// as one set of fields rather than as two conventions stacked.

type Owner = {Name: string; GrantedBy?: string | null};
type Owners = {Positions: Owner[]; UserGroups: Owner[]; WorkGroups: Owner[]};

const EMPTY: Owners = {Positions: [], UserGroups: [], WorkGroups: []};

function Group({label, owners}: {label: string; owners: Owner[]}) {
    return (
        <div style={{display: "flex", flexDirection: "column", gap: 1, minWidth: 220}}>
            <Typography style={{fontSize: 12, color: Grey5, lineHeight: 1.4}}>{label}</Typography>
            {owners.length === 0 ? (
                <Typography style={{fontSize: 15, fontWeight: 400, color: Grey17, lineHeight: 1.45}}>
                    —
                </Typography>
            ) : null}
            {owners.map(owner => (
                <Typography key={owner.Name} style={{fontSize: 15, color: Grey17, lineHeight: 1.45}}>
                    {owner.Name}
                    {/* WHY THEY CAN SEE IT, where the platform granted the share itself. A share
                        somebody made by hand carries nothing, which is the honest distinction --
                        those are the deliberate ones. */}
                    {owner.GrantedBy ? (
                        <span style={{fontSize: 12, color: Grey7}}> · {owner.GrantedBy}</span>
                    ) : null}
                </Typography>
            ))}
        </div>
    );
}

export default function SecondaryOwners({objectRevisionUSID, sessionContext}:
                                        {objectRevisionUSID?: string; sessionContext: any}) {
    const [owners, setOwners] = useState<Owners>(EMPTY);
    // Memoised, or the effect re-runs on every render and fetches forever.
    const api = useMemo(() => makeCresAxiosFoundationAPI(sessionContext), [sessionContext]);

    useEffect(() => {
        if (!objectRevisionUSID) return;
        let cancelled = false;
        api({method: "GET", url: "t1/v1/object_secondary_owners",
             params: {ObjectRevisionUSID: objectRevisionUSID}})
            // COALESCED, not just caught: a response of an unexpected shape must not leave this
            // undefined and take the detail page down with it on the next render.
            .then(response => { if (!cancelled) setOwners({...EMPTY, ...(response.data ?? {})}); })
            // QUIET ON FAILURE. The record itself is on screen and readable; an error box here
            // would make a supplementary line look like the page had broken.
            .catch(() => { if (!cancelled) setOwners(EMPTY); })
        return () => { cancelled = true; };
    }, [api, objectRevisionUSID]);

    return (
        <div style={{display: "flex", flexWrap: "wrap", gap: 40}}>
            <Group label="Secondary Positions" owners={owners.Positions}/>
            <Group label="Secondary User Groups" owners={owners.UserGroups}/>
            <Group label="Secondary Work Groups" owners={owners.WorkGroups}/>
        </div>
    );
}
